import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "~/atoms/dialog";
import { Button } from "~/atoms/button";
import { Checkbox } from "~/atoms/checkbox";
import { Label } from "~/atoms/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { type DocumentVersionSummary } from "~/api/types";
import { toast } from "react-toastify";
import { formatDate } from "~/helpers/date";

interface Props {
  trigger: ReactNode;
  documentVersions: DocumentVersionSummary[];
}

export function ExportDataModal({ trigger, documentVersions }: Props) {
  const { t } = useTranslation();
  const [documentVersionFields, setDocumentVersionFields] = useState({
    id: true,
    title: true,
    status: true,
    reviewFrom: true,
    reviewTo: true,
    fileUpdatedAt: true,
    createdAt: true,
    publishedAt: true,
    adoptedAt: true,
    archivedAt: true,
  });

  const [documentFields, setDocumentFields] = useState({
    owner: true,
    tier: true,
    category: true,
    scheduleReviewFrom: true,
    scheduleReviewTo: true,
  });

  const [isOpen, setIsOpen] = useState(false);

  const handleExport = () => {
    const selectedData = documentVersions.map((dv) => {
      const result: Record<string, string | number> = {};
      if (documentVersionFields.id) result.id = dv.id;
      if (documentVersionFields.title) result.title = dv.title;
      if (documentVersionFields.status) result.status = dv.status;
      if (documentVersionFields.reviewFrom)
        result.reviewFrom = formatDate(dv.reviewFrom, "date");
      if (documentVersionFields.reviewTo)
        result.reviewTo = formatDate(dv.reviewTo, "date");
      if (documentVersionFields.fileUpdatedAt)
        result.fileUpdatedAt = formatDate(dv.fileUpdatedAt, "date");
      if (documentVersionFields.createdAt)
        result.createdAt = formatDate(dv.createdAt, "date");
      if (documentVersionFields.publishedAt)
        result.publishedAt = formatDate(dv.publishedAt, "date");
      if (documentVersionFields.adoptedAt)
        result.adoptedAt = formatDate(dv.adoptedAt, "date");
      if (documentVersionFields.archivedAt)
        result.archivedAt = formatDate(dv.archivedAt, "date");
      if (documentFields.owner)
        result.owner = `${dv.document.owner.firstName} ${dv.document.owner.lastName}`;
      if (documentFields.tier) result.tier = dv.document.tier.title;
      if (documentFields.category) result.category = dv.document.category.title;
      if (documentFields.scheduleReviewFrom)
        result.scheduleReviewFrom = formatDate(
          dv.document.scheduleReviewFrom,
          "date",
        );
      if (documentFields.scheduleReviewTo)
        result.scheduleReviewTo = formatDate(
          dv.document.scheduleReviewTo,
          "date",
        );
      return result;
    });

    if (selectedData.length === 0 || !selectedData[0]) {
      toast.info(t("toasts.info.noDocumentsFound"));
      return;
    }

    const isWindows = navigator.userAgent.includes("Windows");
    const delimiter = isWindows ? "\t" : ";";

    const escapeValue = (value: string | number): string => {
      const str = String(value);
      if (str.includes(delimiter) || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headers = Object.keys(selectedData[0]).join(delimiter);
    const rows = selectedData.map((row) =>
      Object.values(row).map(escapeValue).join(delimiter),
    );

    const csv = [headers, ...rows].join("\r\n");

    let blob: Blob;
    if (isWindows) {
      const utf16Array = new Uint16Array(csv.length);
      for (let i = 0; i < csv.length; i++) {
        utf16Array[i] = csv.charCodeAt(i);
      }
      const BOM = new Uint8Array([0xff, 0xfe]);
      blob = new Blob([BOM, utf16Array], { type: "text/csv;charset=utf-16le" });
    } else {
      const BOM = "\uFEFF";
      blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8" });
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "document-metadata-export.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setIsOpen(false);
  };

  const toggleVersionField = (field: string) => {
    setDocumentVersionFields((prev) => ({
      ...prev,
      [field]: !prev[field as keyof typeof prev],
    }));
  };

  const toggleDocumentField = (field: string) => {
    setDocumentFields((prev) => ({
      ...prev,
      [field]: !prev[field as keyof typeof prev],
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {t("commons.workflow.exportMetadata")}
          </DialogTitle>
        </DialogHeader>
        <Card>
          <CardHeader>
            <CardTitle>{t("commons.workflow.filterDocuments")}</CardTitle>
            <CardDescription>
              {t("commons.workflow.filterDocumentsDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {t("library.filters.selectedCount", {
              count: documentVersions.length,
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("commons.workflow.selectFields")}</CardTitle>
            <CardDescription>
              {t("commons.workflow.selectFieldsDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {Object.entries(documentVersionFields).flatMap(([key, value]) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={key}
                    checked={value}
                    onCheckedChange={() => toggleVersionField(key)}
                  />
                  <Label htmlFor={key}>{key}</Label>
                </div>
              ))}
              {Object.entries(documentFields).flatMap(([key, value]) => (
                <div key={key} className="flex items-center space-x-2">
                  <Checkbox
                    id={key}
                    checked={value}
                    onCheckedChange={() => toggleDocumentField(key)}
                  />
                  <Label htmlFor={key}>{key}</Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            {t("commons.workflow.cancel")}
          </Button>
          <Button onClick={handleExport} className="gap-2">
            {t("commons.workflow.exportAsCsv")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
